import { useEffect, useRef, useState } from "react";
import { Bookmark, Plus, Trash2, X, Check, Share2 } from "lucide-react";

const STORAGE_KEY = "cpr.schedule.pinnedViews.v1";

function loadPresets() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch (_) {
        return [];
    }
}

function savePresets(presets) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch (_) {
        /* quota / private-mode — ignore */
    }
}

/** Pinned view manager for the schedule page.
 *
 * Storage: persists to the user's profile via PUT /api/me/preferences and
 * mirrors to localStorage for instant first paint. The server copy wins on
 * conflict (e.g. switching devices).
 *
 * Props:
 *   currentView       — "month" | "week" | "day"
 *   currentCrewIds    — Set<string>
 *   crewsById         — Map<id, crew>
 *   activePresetId    — id of the currently applied preset (or null)
 *   onApply(preset)   — apply a saved preset → caller sets view + selectedCrewIds
 *   onClearActive()   — fires when the user clicks the active preset to unpin
 *   onShare(preset)   — caller copies a sharable URL for the preset
 *   serverPresets     — initial list from /api/me/preferences (null while loading)
 *   onPersist(list)   — fires when presets change so caller can PUT to server
 */
export default function PinnedViews({
    currentView,
    currentCrewIds,
    crewsById,
    activePresetId,
    onApply,
    onClearActive,
    onShare,
    serverPresets,
    onPersist,
}) {
    const [presets, setPresets] = useState(() => loadPresets());
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const lastPersistedRef = useRef(null);

    // Hydrate from server once it arrives — server wins
    useEffect(() => {
        if (Array.isArray(serverPresets)) {
            setPresets(serverPresets);
            savePresets(serverPresets);
            lastPersistedRef.current = JSON.stringify(serverPresets);
        }
        // eslint-disable-next-line
    }, [serverPresets]);

    useEffect(() => {
        savePresets(presets);
        const serialised = JSON.stringify(presets);
        if (onPersist && serialised !== lastPersistedRef.current) {
            lastPersistedRef.current = serialised;
            onPersist(presets);
        }
    }, [presets, onPersist]);

    const pin = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const preset = {
            id: `pv-${Date.now()}`,
            name: trimmed,
            view: currentView,
            crewIds: Array.from(currentCrewIds || []),
            created_at: new Date().toISOString(),
        };
        setPresets((prev) => [preset, ...prev].slice(0, 12));
        setName("");
        setAdding(false);
    };

    const remove = (id) => {
        setPresets((prev) => prev.filter((p) => p.id !== id));
    };

    return (
        <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="pinned-views"
        >
            <span className="text-xs text-muted font-display font-bold uppercase tracking-wider mr-1">
                Pinned:
            </span>

            {presets.length === 0 && !adding && (
                <span className="text-xs text-muted italic">No saved views yet.</span>
            )}

            {presets.map((p) => {
                const isActive = activePresetId === p.id;
                const previewCrews = (p.crewIds || [])
                    .slice(0, 2)
                    .map((cid) => crewsById.get(cid)?.name || crewsById.get(cid)?.email)
                    .filter(Boolean)
                    .join(", ");
                const more = (p.crewIds || []).length - 2;
                const subtitle =
                    p.crewIds.length === 0
                        ? "All crews"
                        : `${previewCrews}${more > 0 ? ` +${more}` : ""}`;
                return (
                    <span
                        key={p.id}
                        className="inline-flex items-center"
                        data-testid={`pinned-chip-${p.id}`}
                    >
                        <button
                            type="button"
                            className={`btn ${isActive ? "btn-primary" : "btn-secondary"}`}
                            style={{ paddingRight: "0.5rem" }}
                            onClick={() => (isActive ? onClearActive?.() : onApply(p))}
                            title={`${p.view} · ${subtitle}`}
                            data-testid={`pinned-apply-${p.id}`}
                        >
                            {isActive ? <Check size={12} /> : <Bookmark size={12} />}
                            <span className="text-sm">{p.name}</span>
                            <span className="text-xs opacity-70 ml-1 capitalize">
                                · {p.view}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onShare?.(p)}
                            className="text-xs opacity-50 hover:opacity-100 ml-1 px-1"
                            aria-label={`Share pinned view ${p.name}`}
                            data-testid={`pinned-share-${p.id}`}
                            title="Copy share link"
                        >
                            <Share2 size={11} />
                        </button>
                        <button
                            type="button"
                            onClick={() => remove(p.id)}
                            className="text-xs opacity-50 hover:opacity-100 ml-1 px-1"
                            aria-label={`Remove pinned view ${p.name}`}
                            data-testid={`pinned-remove-${p.id}`}
                        >
                            <Trash2 size={11} />
                        </button>
                    </span>
                );
            })}

            {!adding ? (
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setAdding(true)}
                    data-testid="pin-view-btn"
                >
                    <Plus size={12} />
                    Pin this view
                </button>
            ) : (
                <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md"
                    style={{
                        background: "var(--brand-white, #fff)",
                        border: "1px solid rgba(0,0,0,0.12)",
                    }}
                >
                    <input
                        type="text"
                        value={name}
                        autoFocus
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") pin();
                            if (e.key === "Escape") {
                                setAdding(false);
                                setName("");
                            }
                        }}
                        placeholder="Name this view…"
                        className="bg-transparent outline-none text-sm"
                        style={{ width: 160 }}
                        data-testid="pin-view-name-input"
                    />
                    <button
                        type="button"
                        className="link-red text-xs font-bold"
                        onClick={pin}
                        disabled={!name.trim()}
                        data-testid="pin-view-save"
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        className="opacity-60 hover:opacity-100"
                        onClick={() => {
                            setAdding(false);
                            setName("");
                        }}
                        aria-label="Cancel"
                        data-testid="pin-view-cancel"
                    >
                        <X size={12} />
                    </button>
                </span>
            )}
        </div>
    );
}
